import { Pencil } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { AssinaturaCard } from '@/features/perfil/components/AssinaturaCard';
import { ImagensDoPerfil } from '@/features/perfil/components/ImagensDoPerfil';
import { PerfilFormDialogo } from '@/features/perfil/components/PerfilFormDialogo';
import { formatarEndereco } from '@/shared/lib/endereco';
import { EstadoCarregando } from '@/shared/components/estados';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

/**
 * Perfil do usuário da sessão.
 *
 * O que fica de fora da edição, e por quê:
 *
 * - **E-mail e número do CRMV** não estão no `AtualizarMeDto`, que é `.strict()`
 *   — o DTO restrito existe para barrar campo privilegiado, e mandar chave a
 *   mais responde 422. Só a UF do CRMV é editável.
 * - **Trocar a senha** não tem caminho: `change-password` não está na allowlist
 *   do `auth.mount.ts` (das ~30 rotas do better-auth, 8 estão expostas). O
 *   fluxo que existe é o de recuperação por e-mail, que depende do INF-5.
 *
 * Foto, logo e assinatura NÃO entram no diálogo de edição: cada um tem rota
 * própria em `/v1/me` e salva sozinho, porque upload não combina com um
 * formulário que só grava no "Salvar" — o arquivo já subiu.
 *
 * Sem guarda de permissão de propósito: `/perfil` está fora do catálogo
 * (PLANO-MVP §4) e a rota do backend é `@ForaDoCatalogo`.
 */
export function PerfilPage() {
  const { me, carregando } = useAuth();
  const [editando, setEditando] = useState(false);

  if (carregando || !me) return <EstadoCarregando />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{me.perfil.nomeCompleto}</h2>
          <p className="text-sm text-muted-foreground">{me.usuario.email}</p>
        </div>
        <div className="flex items-center gap-2">
          {me.superAdmin && <Badge className="bg-brand text-brand-foreground">Super Admin</Badge>}
          {me.cargo && <Badge variant="secondary">{me.cargo.nome}</Badge>}
          <Button onClick={() => setEditando(true)}>
            <Pencil className="w-4 h-4" />
            Editar perfil
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-surface-raised border-2 border-brand/20">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-brand uppercase tracking-wide">
              Dados profissionais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Linha rotulo="CRMV" valor={me.perfil.crmv ? `${me.perfil.crmv}${me.perfil.ufCrmv ? `-${me.perfil.ufCrmv}` : ''}` : null} />
            <Linha rotulo="Especialidade" valor={me.perfil.especialidade} />
            <Linha rotulo="Clínica" valor={me.perfil.clinica} />
            <Linha rotulo="Telefone" valor={me.perfil.telefone} />
            <Linha rotulo="Endereço" valor={formatarEndereco(me.perfil.endereco)} />
          </CardContent>
        </Card>

        <Card className="bg-surface-raised border-2 border-brand/20">
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-brand uppercase tracking-wide">
              Ambiente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Linha rotulo="Nome" valor={me.tenant.nome} />
            <Linha rotulo="Tipo" valor={me.tenant.tipo} />
            <Linha rotulo="Identificador" valor={me.tenant.slug} />
            <Linha rotulo="Permissões" valor={`${me.permissoes.length} concedidas`} />
          </CardContent>
        </Card>
      </div>

      <Card className="bg-surface-raised border-2 border-brand/20">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-brand uppercase tracking-wide">
            Descrição
          </CardTitle>
        </CardHeader>
        <CardContent>
          {me.perfil.bio ? (
            <p className="text-sm text-foreground whitespace-pre-wrap">{me.perfil.bio}</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {me.cargo?.chave === 'assessor'
                ? 'Nenhuma descrição cadastrada ainda — é o que o médico vê antes de escolher você como especialista.'
                : 'Nenhuma descrição cadastrada ainda.'}
            </p>
          )}
        </CardContent>
      </Card>

      <ImagensDoPerfil me={me} />

      <AssinaturaCard me={me} />

      <PerfilFormDialogo aberto={editando} aoFechar={() => setEditando(false)} me={me} />
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor?: string | null }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{rotulo}</span>
      <span className="font-medium text-foreground text-right">{valor ?? '—'}</span>
    </div>
  );
}
